import React, { useCallback, useMemo } from "react"
import styled from "styled-components"
import { State } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { EMPTY_PRODUCT, useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { useLanguageState } from "../../globalStates/LanguageState"
import { addElement, removeElementById } from "../../globalStates/utils"
import { newId } from "../../utils/Id"
import { repositionItem } from "../../utils/Arrays"
import Page from "../../components/Page"
import Button from "../../components/Button"
import ProductSection from "./ProductSection"
import { useConfigDataState } from "../../globalStates/ConfigDataState"
import { Product } from "../../globalStates/ExhibitorDataTypes"

const ButtonsBar = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`

function sortProducts(productStates: State<Product[], Validation>): State<Product, Validation>[] {
    const products = productStates.map((productState) => productState)
    return products.sort((p1, p2) => p1.orderProduct.get() - p2.orderProduct.get())
}

const ProductsPage: React.FC<{}> = () => {
    const branding = useLanguageState().getStrings()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const productsLimit = serviceKeys.products || 0
    const productStates = useExhibitorDataState().getProducts()
    const orderedProductStates = useMemo(() => sortProducts(productStates), [productStates])
    const isMonoLingual = branding.configuration.monoLingual
    const canAddProduct = useMemo(() => {
        if (productStates.length >= productsLimit) return false
        let valid = true
        productStates.forEach((productState) => {
            const name = productState.name.get()
            const nameDe = productState.nameDe.get()
            isMonoLingual ? (valid = valid && !!nameDe && name.length >= 0) : (valid = valid && !!name && name.length >= 0)
        })
        return valid
    }, [productStates, productsLimit, isMonoLingual])

    const onAddProduct = useCallback(() => {
        addElement<Product>(productStates, {
            ...EMPTY_PRODUCT,
            id: newId().toString(),
            orderProduct: productStates.length + 1,
            medias: []
        })
    }, [productStates])

    const onRemoveProduct = useCallback(
        (productId: string) => {
            removeElementById<Product>(productStates, productId)
        },
        [productStates]
    )

    const onOrderChanged = useCallback(
        (productId: string, newIndex: number) => {
            const oldIndex = orderedProductStates.findIndex((productState) => productState.id.get() === productId)
            const repositionedProductStates = repositionItem(
                orderedProductStates,
                orderedProductStates[oldIndex],
                oldIndex,
                newIndex
            )
            repositionedProductStates.forEach((product, index) => {
                if (product.orderProduct.get() !== index + 1) product.orderProduct.set(index + 1)
            })
        },
        [orderedProductStates]
    )

    return (
        <Page
            title={branding.configuration.productsPage.pageTitle}
            description={branding.configuration.productsPage.pageDescription}
        >
            {orderedProductStates?.map((productState, index) => (
                <ProductSection
                    key={productState.id.get()}
                    productState={productState}
                    index={index}
                    onOrderChanged={onOrderChanged}
                    onClickRemove={onRemoveProduct}
                />
            ))}

            <ButtonsBar>
                <Button
                    id="addProduct"
                    caption={branding.configuration.productsPage.btnAddProduct}
                    inverse
                    onClick={onAddProduct}
                    disabled={!canAddProduct}
                />
            </ButtonsBar>
        </Page>
    )
}

export default ProductsPage
